import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { inviteAPI } from '../services/api';
import './Auth.css';

const Register = () => {
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('token');

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    confirmPassword: '',
    department: '',
    employee_id: '',
  });
  const [inviteData, setInviteData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  // Load reCAPTCHA script
  useEffect(() => {
    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || 'YOUR_SITE_KEY';

    if (document.querySelector(`script[src*="recaptcha"]`)) {
      setRecaptchaReady(true);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      const checkReady = setInterval(() => {
        if (window.grecaptcha && window.grecaptcha.ready) {
          window.grecaptcha.ready(() => {
            setRecaptchaReady(true);
            clearInterval(checkReady);
          });
        }
      }, 100);
    };
    document.head.appendChild(script);
  }, []);

  // Validate invite token
  useEffect(() => {
    if (!inviteToken) {
      setError('No invitation token provided. Registration is invite-only.');
      setValidating(false);
      return;
    }

    const validateInvite = async () => {
      try {
        const response = await inviteAPI.validateInvite(inviteToken);
        setInviteData(response.data);
        setValidating(false);
      } catch (err) {
        setError(
          err.response?.data?.error ||
            'Invalid or expired invitation. Please contact your administrator for a new invitation.'
        );
        setValidating(false);
      }
    };

    validateInvite();
  }, [inviteToken]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const getRecaptchaToken = async () => {
    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || 'YOUR_SITE_KEY';

    if (!window.grecaptcha || !recaptchaReady) {
      console.warn('reCAPTCHA not ready, proceeding without token');
      return null;
    }

    try {
      const token = await window.grecaptcha.execute(siteKey, { action: 'register' });
      return token;
    } catch (error) {
      console.error('reCAPTCHA error:', error);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    try {
      const recaptchaToken = await getRecaptchaToken();

      const { confirmPassword, ...registerData } = formData;
      const response = await inviteAPI.registerWithInvite({
        token: inviteToken,
        ...registerData,
        recaptchaToken,
      });

      // Login automatically after registration
      login(response.data.user);
      navigate('/dashboard');
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Registration failed. Please try again.';
      setError(errorMessage);

      if (errorMessage.includes('reCAPTCHA') || errorMessage.includes('bot')) {
        setError(
          'Security verification failed. Please refresh the page and try again.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="auth-container">
        <div className="auth-backdrop"></div>
        <div className="auth-card fade-in">
          <div className="auth-header">
            <h1 className="auth-title">Validating Invitation...</h1>
            <p className="auth-subtitle">Please wait</p>
          </div>
        </div>
      </div>
    );
  }

  if (!inviteToken || (error && !inviteData)) {
    return (
      <div className="auth-container">
        <div className="auth-backdrop"></div>
        <div className="auth-card fade-in">
          <div className="auth-header">
            <h1 className="auth-title">Invitation Required</h1>
            <p className="auth-subtitle">Registration is invite-only</p>
          </div>

          <div className="error-message">{error}</div>

          <div style={{ textAlign: 'center', marginTop: '24px' }}>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              To create an account, you need an invitation link from an administrator.
            </p>
            <button onClick={() => navigate('/login')} className="btn btn-primary">
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-backdrop"></div>
      <div className="auth-card fade-in">
        <div className="auth-header">
          <h1 className="auth-title">Create Account</h1>
          <p className="auth-subtitle">Complete your registration</p>
        </div>

        {inviteData && (
          <div className="info-message" style={{ marginBottom: '24px' }}>
            <strong>Mobile Number:</strong> {inviteData.mobile_number}
            <br />
            <small>This will be your username</small>
          </div>
        )}

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label className="form-label">Full Name *</label>
            <input
              type="text"
              name="full_name"
              className="form-input"
              value={formData.full_name}
              onChange={handleChange}
              required
              autoFocus
              placeholder="Enter your full name"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Email *</label>
            <input
              type="email"
              name="email"
              className="form-input"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="Enter your email address"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Password *</label>
            <input
              type="password"
              name="password"
              className="form-input"
              value={formData.password}
              onChange={handleChange}
              required
              minLength={8}
              placeholder="At least 8 characters"
            />
            <small style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>
              Minimum 8 characters
            </small>
          </div>

          <div className="form-group">
            <label className="form-label">Confirm Password *</label>
            <input
              type="password"
              name="confirmPassword"
              className="form-input"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              placeholder="Re-enter your password"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Department (Optional)</label>
            <input
              type="text"
              name="department"
              className="form-input"
              value={formData.department}
              onChange={handleChange}
              placeholder="Your department"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Employee ID (Optional)</label>
            <input
              type="text"
              name="employee_id"
              className="form-input"
              value={formData.employee_id}
              onChange={handleChange}
              placeholder="Your employee ID"
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={loading}
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>

          <div className="recaptcha-notice">
            <small>
              This site is protected by reCAPTCHA and the Google{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>{' '}
              and{' '}
              <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
                Terms of Service
              </a>{' '}
              apply.
            </small>
          </div>
        </form>

        <div className="auth-footer">
          <p>
            Already have an account?{' '}
            <button onClick={() => navigate('/login')} className="auth-link">
              Sign In
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
